import * as Sentry from '@sentry/react';
import { applyUserIdentity } from './identity';

// CPAYMENT-1449: tunnel path to skip ad blockers; must match user-page-back
export const SENTRY_TUNNEL_PATH = '/api/te';

// CPAYMENT-1417: Session Replay error-sampling — always capture sessions that hit an error.
export const REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

// CPAYMENT-1417 / CPAYMENT-1786: Session Replay session-sampling. Every environment now records every
// session. It stays a code constant rather than an env var for the reason it always was one: a typo
// in an environment variable must never be able to change recording in prod by itself. Changing it
// is a redeploy, which is also the rollback if the tunnel or the disk cannot take the volume.
//
// Why 1.0 in prod (CPAYMENT-1786): error-sampling gave ~50 replays a day for ~2100 tickets, and only
// for sessions that raised an error. The receipt-upload failure raised none, so the very sessions
// support asks about were the ones never recorded. Note this also changes `replay_type` from
// `buffer` (a window around an error) to `session` (the whole visit).
//
// Enabled 2026-08-24 for a week of real load, then reviewed: keep, reduce, or narrow to a segment.
const REPLAYS_SESSION_SAMPLE_RATE = 1.0;

export const initSentry = (): void => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT;

  // Replay is initialised only with a valid DSN; failures here must never break Sentry init.
  const buildReplay = () => {
    if (!dsn) return undefined;
    try {
      return Sentry.replayIntegration({
        // Strict masking for a payment page: no PII / payment requisites in replays.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      });
    } catch {
      // Replay must never break the payment flow
      return undefined;
    }
  };

  const replay = buildReplay();
  const integrations = [
    Sentry.browserTracingIntegration(),
    ...(replay ? [replay] : []),
  ];

  Sentry.init({
    dsn,
    environment,
    tunnel: SENTRY_TUNNEL_PATH,
    integrations,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    enableLogs: true,
    beforeSendMetric: (metric) => metric,
  });
  applyUserIdentity();
};
